import { useState, useRef, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { Plus } from "lucide-react";
import { TwemojiImg } from "../TwemojiImg";
import { EMOJI_CATEGORIES } from "../../constants/emojiCategories";
import { EMOJI_KEYWORDS } from "../../constants/emojiKeywords";

// `onSwitch` (optional) adds a "use icon" link at the bottom; `openOnMount` opens
// the picker straight away (used right after the other picker's link switched here).
// `compact`: narrower button (habit form, so the name field gets more room).
export function EmojiPicker({ value, onChange, onSwitch, openOnMount = false, compact = false }) {
  const [open, setOpen] = useState(false);
  const [cat, setCat] = useState("fitness");
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const allEmojis = useMemo(() => {
    const seen = new Set();
    return EMOJI_CATEGORIES.flatMap(c => c.emojis).filter(e => { if (seen.has(e)) return false; seen.add(e); return true; });
  }, []);

  // Search matches against the emoji's keywords (e.g. "heart", "run") — the
  // glyph itself has no text to compare a typed search against, so keyword
  // lookup is the only way this can ever find anything.
  const displayEmojis = search.trim()
    ? (() => {
        const q = search.trim().toLowerCase();
        return allEmojis.filter(e => (EMOJI_KEYWORDS[e] || []).some(kw => kw.includes(q)));
      })()
    : (EMOJI_CATEGORIES.find(c => c.id === cat)?.emojis || []);

  const handleOpen = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow < 340 ? Math.max(8, rect.top - 4 - 340) : rect.bottom + 4;
      const left = Math.min(rect.left, window.innerWidth - 290);
      setDropPos({ top, left });
    }
    setOpen(p => !p);
    setSearch("");
  };

  useEffect(() => {
    if (openOnMount) handleOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button ref={btnRef} onClick={handleOpen}
        style={{ fontSize: 28, background: open ? "var(--c-bg-card)" : "var(--c-bg-card)", border: value ? "1.5px solid color-mix(in srgb, var(--c-primary) 40%, transparent)" : "1.5px dashed color-mix(in srgb, var(--c-primary) 40%, transparent)", borderRadius: 12, padding: compact ? "6px 8px" : "6px 12px", cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", minWidth: compact ? 44 : 52 }}>
        {value ? <TwemojiImg emoji={value} size={28} /> : <Plus size={20} style={{ color: "var(--c-text-soft)" }} />}
      </button>
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 19990 }} />
          <div className="clarity-emoji-picker" style={{ position: "fixed", zIndex: 19999, top: dropPos.top, left: dropPos.left, background: "var(--c-bg-card)", border: "1.5px solid color-mix(in srgb, var(--c-primary) 40%, transparent)", borderRadius: 16, padding: 10, boxShadow: "0 8px 32px var(--c-shadow)", width: 280, boxSizing: "border-box" }}>

          {/* Search bar */}
          <input
            placeholder="🔍 Search emoji..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: "6px 10px", borderRadius: 10, border: "1.5px solid color-mix(in srgb, var(--c-primary) 40%, transparent)", background: "var(--c-bg-card)", marginBottom: 8, fontFamily: "inherit", outline: "none", color: "var(--c-text)" }}
          />

          {/* Category tabs — hidden while searching */}
          {!search.trim() && (
            <div style={{ display: "flex", flexWrap: "nowrap", overflowX: "auto", gap: 4, marginBottom: 8, borderBottom: "1px solid color-mix(in srgb, var(--c-primary) 40%, transparent)", paddingBottom: 6, scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {EMOJI_CATEGORIES.map(c => (
                <button key={c.id} onClick={() => setCat(c.id)} title={c.label}
                  style={{ background: "transparent", border: "none", borderRadius: 8, padding: "3px 8px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 10, color: cat === c.id ? "var(--c-text-accent)" : "var(--c-text-soft)", fontWeight: cat === c.id ? 700 : 400, textDecoration: cat === c.id ? "underline" : "none", textDecorationThickness: 2, textUnderlineOffset: 4 }}>
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {/* Emoji grid */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 3, maxHeight: 210, overflowY: "auto", overflowX: "hidden" }}>
            {displayEmojis.map(e => (
              <button key={e} onClick={() => { onChange(e); setOpen(false); setSearch(""); }}
                style={{ background: value === e ? "var(--c-bg-card)" : "transparent", border: value === e ? "1.5px solid var(--c-primary)" : "1.5px solid transparent", borderRadius: 8, padding: "4px 2px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 32 }}>
                <TwemojiImg emoji={e} size={20} />
              </button>
            ))}
            {displayEmojis.length === 0 && (
              <p style={{ gridColumn: "1/-1", textAlign: "center", fontSize: 11, color: "var(--c-text-soft)", margin: "10px 0", fontStyle: "italic" }}>No emojis found 😔</p>
            )}
          </div>

          {onSwitch && <button type="button" onClick={() => { setOpen(false); onSwitch(); }}
              style={{ display: "block", margin: "8px auto 0", padding: "2px 4px", background: "none", border: "none", cursor: "pointer", color: "var(--c-text-accent)", fontFamily: "inherit", fontSize: 13, fontWeight: 800, textDecoration: "underline" }}>
              use icon
            </button>}
        </div>
        </>,
        document.body
      )}
    </div>
  );
}
